import React, { useState, useEffect } from 'react';
import {
    Modal,
    Form,
    Input,
    Button,
    message,
    Space,
    Typography,
    List,
    Tag,
    Tooltip,
    Spin,
    Card,
    Divider,
    Alert,
    Row,
    Col
} from 'antd';
import {
    UserAddOutlined,
    MailOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    InfoCircleOutlined,
    UserOutlined,
    TeamOutlined
} from '@ant-design/icons';
import auctionService from '../../services/auctionService';

const { Text, Title } = Typography;

const InviteParticipants = ({ auction, visible, onClose, onSuccess }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [participants, setParticipants] = useState([]);
    const [loadingParticipants, setLoadingParticipants] = useState(true);

    useEffect(() => {
        if (visible && auction) {
            loadParticipants();
        }
    }, [visible, auction]);

    const loadParticipants = async () => {
        try {
            setLoadingParticipants(true);
            const response = await auctionService.getParticipants(auction.slug);
            setParticipants(response.data);
        } catch (err) {
            message.error('Failed to load participants');
        } finally {
            setLoadingParticipants(false);
        }
    };

    const handleInvite = async (values) => {
        try {
            setLoading(true);
            const response = await auctionService.inviteParticipant(auction.slug, {
                email: values.email,
                first_name: values.first_name,
                last_name: values.last_name
            });
            
            if (response.data.is_new_user) {
                message.success('Invitation sent! The user will receive an email to complete their registration.');
            } else {
                message.success('Participant invited successfully');
            }
            
            form.resetFields();
            await loadParticipants();
            
            if (onSuccess) {
                onSuccess();
            }
        } catch (err) {
            message.error(err.response?.data?.message || 'Failed to invite participant');
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveParticipant = async (participantId) => {
        try {
            setLoading(true);
            await auctionService.removeParticipant(auction.slug, participantId);
            message.success('Participant removed successfully');
            await loadParticipants();
            
            if (onSuccess) {
                onSuccess();
            }
        } catch (err) {
            message.error(err.response?.data?.message || 'Failed to remove participant');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            invited: 'default',
            accepted: 'success',
            declined: 'error'
        };
        return colors[status] || 'default';
    };

    return (
        <Modal
            title={null}
            open={visible}
            onCancel={onClose}
            footer={null}
            centered
            width="90%"
            style={{ maxWidth: '1000px', padding: 0 }}
            closable={true}
            maskClosable={true}
            className="invite-participants-modal"
        >
            <div style={{ 
                background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)',
                borderRadius: '12px',
                overflow: 'hidden'
            }}>
                {/* Header Section */}
                <div className="invite-modal-header" style={{
                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                    padding: '20px 24px 16px 24px',
                    textAlign: 'center',
                    borderBottom: 'none'
                }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        margin: '0 auto 12px',
                        background: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }}>
                        <UserAddOutlined style={{ fontSize: '24px', color: 'white' }} />
                    </div>
                    <Title level={4} style={{ 
                        color: 'white', 
                        margin: 0,
                        fontWeight: 600,
                        fontSize: '20px'
                    }}>
                        Invite Participants
                    </Title>
                    <span style={{ 
                        color: 'rgba(255, 255, 255, 0.9)', 
                        fontSize: '13px',
                        display: 'block',
                        marginTop: '6px'
                    }}>
                        Send invitations to participants for your private auction
                    </span>
                </div>

                {/* Content Section - Side by Side */}
                <div className="invite-modal-content" style={{ padding: '24px' }}>
                    <Row gutter={[20, 20]}>
                        {/* Left Column - Add New Participant Form */}
                        <Col xs={24} md={12}>
                <Card 
                    className="form-section"
                    style={{ 
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                        border: '1px solid #e8f4f8',
                        transition: 'all 0.3s ease',
                        overflow: 'hidden',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                    bodyStyle={{ 
                        padding: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%'
                    }}
                >
                    <div style={{ 
                        borderBottom: '2px solid #f0f7ff',
                        paddingBottom: 12,
                        marginBottom: 20,
                        background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)',
                        margin: '-20px -20px 20px -20px',
                        padding: '16px 20px'
                    }}>
                        <Space align="center" size="small">
                            <div style={{
                                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                padding: '8px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                            }}>
                                <UserAddOutlined style={{ color: 'white', fontSize: '18px' }} />
                            </div>
                            <div>
                                <Title level={5} style={{ margin: 0, color: '#1a1a1a', fontWeight: 600 }}>
                                    Add New Participant
                                </Title>
                                <span style={{ fontSize: '13px', color: '#595959', display: 'block', marginTop: 2 }}>
                                    Enter participant details to send an invitation
                                </span>
                            </div>
                        </Space>
                    </div>

                    <Form
                        form={form}
                        onFinish={handleInvite}
                        layout="vertical"
                        size="large"
                    >
                        <Form.Item
                            name="email"
                            label={<span style={{ fontWeight: 500, fontSize: '14px' }}>Email Address</span>}
                            rules={[
                                { required: true, message: 'Please enter an email address' },
                                { type: 'email', message: 'Please enter a valid email address' }
                            ]}
                            extra={
                                <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                    The invitation will be sent to this email address
                                </span>
                            }
                        >
                            <Input 
                                prefix={<MailOutlined style={{ color: '#1890ff' }} />}
                                placeholder="participant@example.com"
                                style={{ 
                                    borderRadius: '8px'
                                }}
                            />
                        </Form.Item>

                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    name="first_name"
                                    label={<span style={{ fontWeight: 500, fontSize: '14px' }}>First Name</span>}
                                    extra={
                                        <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                            Optional
                                        </span>
                                    }
                                >
                                    <Input 
                                        prefix={<UserOutlined style={{ color: '#1890ff' }} />}
                                        placeholder="Enter first name"
                                        style={{ 
                                            borderRadius: '8px'
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    name="last_name"
                                    label={<span style={{ fontWeight: 500, fontSize: '14px' }}>Last Name</span>}
                                    extra={
                                        <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                            Optional
                                        </span>
                                    }
                                >
                                    <Input 
                                        prefix={<UserOutlined style={{ color: '#1890ff' }} />}
                                        placeholder="Enter last name"
                                        style={{ 
                                            borderRadius: '8px'
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider style={{ margin: '20px 0', borderColor: '#e8f4f8' }} />

                        <div style={{ 
                            textAlign: 'center', 
                            marginTop: '4px'
                        }}>
                            <Button 
                                type="primary" 
                                htmlType="submit" 
                                loading={loading}
                                icon={<UserAddOutlined />}
                                size="large"
                                style={{
                                    minWidth: 180,
                                    height: '40px',
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                                }}
                            >
                                {loading ? 'Sending...' : 'Send Invitation'}
                            </Button>
                            <div style={{ marginTop: '8px' }}>
                                <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                    The participant will receive an email with instructions to join
                                </span>
                            </div>
                        </div>
                    </Form>
                </Card>
                        </Col>

                        {/* Right Column - Current Participants List */}
                        <Col xs={24} md={12}>
                            <Card 
                                style={{ 
                                    borderRadius: '12px',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                                    border: '1px solid #e8f4f8',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}
                                bodyStyle={{ 
                                    padding: '20px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    height: '100%'
                                }}
                            >
                                <div style={{ 
                                    borderBottom: '2px solid #f0f7ff',
                                    paddingBottom: 12,
                                    marginBottom: 20,
                                    background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)',
                                    margin: '-20px -20px 20px -20px',
                                    padding: '16px 20px'
                                }}>
                                    <Space align="center" size="small">
                                        <div style={{
                                            background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                                            padding: '8px',
                                            borderRadius: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 2px 8px rgba(82, 196, 26, 0.3)'
                                        }}>
                                            <TeamOutlined style={{ color: 'white', fontSize: '18px' }} />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <Title level={5} style={{ margin: 0, color: '#1a1a1a', fontWeight: 600 }}>
                                                Current Participants
                                            </Title>
                                            <span style={{ fontSize: '12px', color: '#595959', display: 'block', marginTop: 2 }}>
                                                {participants.length} {participants.length === 1 ? 'participant' : 'participants'} invited
                                            </span>
                                        </div>
                                    </Space>
                                </div>

                                <div style={{ flex: 1, overflowY: 'auto', maxHeight: '500px' }}>
                    
                    {loadingParticipants ? (
                        <div style={{ textAlign: 'center', padding: '16px' }}>
                            <Spin />
                        </div>
                    ) : participants.length === 0 ? (
                        <Card 
                            bordered={false}
                            style={{ 
                                background: '#fafafa',
                                borderRadius: '8px',
                                textAlign: 'center',
                                padding: '24px 16px',
                                border: '1px solid #e8e8e8'
                            }}
                        >
                            <UserOutlined style={{ fontSize: '36px', color: '#bfbfbf', marginBottom: '12px' }} />
                            <span style={{ fontSize: '14px', display: 'block', color: '#8c8c8c' }}>
                                No participants have been invited yet
                            </span>
                            <span style={{ fontSize: '12px', display: 'block', marginTop: 4, color: '#8c8c8c' }}>
                                Start inviting participants using the form above
                            </span>
                        </Card>
                    ) : (
                        <List
                            dataSource={participants}
                            renderItem={participant => (
                                <List.Item
                                    style={{
                                        background: '#fff',
                                        marginBottom: 8,
                                        padding: '12px 16px',
                                        borderRadius: '8px',
                                        border: '1px solid #e8f4f8',
                                        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.04)',
                                        transition: 'all 0.3s ease'
                                    }}
                                    actions={[
                                        <Tooltip title="Remove Participant">
                                            <Button
                                                type="text"
                                                danger
                                                icon={<CloseCircleOutlined />}
                                                onClick={() => handleRemoveParticipant(participant.id)}
                                                loading={loading}
                                                size="middle"
                                                style={{ borderRadius: '6px' }}
                                            />
                                        </Tooltip>
                                    ]}
                                >
                                    <List.Item.Meta
                                        avatar={
                                            <div style={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                boxShadow: '0 2px 6px rgba(24, 144, 255, 0.3)'
                                            }}>
                                                <UserOutlined style={{ fontSize: '20px', color: 'white' }} />
                                            </div>
                                        }
                                        title={
                                            <Space wrap>
                                                {participant.first_name && participant.last_name ? (
                                                    <span style={{ fontSize: '14px', fontWeight: 600 }}>
                                                        {participant.first_name} {participant.last_name}
                                                    </span>
                                                ) : (
                                                    <span style={{ fontSize: '14px', fontWeight: 600 }}>
                                                        {participant.email}
                                                    </span>
                                                )}
                                                <Tag 
                                                    color={getStatusColor(participant.status)}
                                                    style={{ borderRadius: '6px', fontSize: '11px', fontWeight: 500 }}
                                                >
                                                    {participant.status.toUpperCase()}
                                                </Tag>
                                            </Space>
                                        }
                                        description={
                                            <Space direction="vertical" size={2} style={{ marginTop: 6 }}>
                                                <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                                    {participant.email}
                                                </span>
                                                {participant.status === 'invited' && (
                                                    <span style={{ fontSize: '11px', color: '#8c8c8c' }}>
                                                        Invited: {new Date(participant.invited_at).toLocaleDateString()}
                                                    </span>
                                                )}
                                            </Space>
                                        }
                                    />
                                </List.Item>
                            )}
                        />
                    )}
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </div>
            <style>{`
                .invite-participants-modal .ant-modal-content {
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
                }
                
                .invite-participants-modal .ant-modal-close {
                    display: flex;
                    font-size: 16px;
                    font-style: normal;
                    line-height: 32px;
                    justify-content: center;
                    text-transform: none;
                    text-rendering: auto;
                    color: #ffffff;
                    padding: 10px;
                    background: #333333;
                    border-radius: 50%;
                    width: 30px;
                    height: 30px;
                    top: 12px;
                    right: 12px;
                }
                
                .invite-participants-modal .ant-modal-close:hover {
                    color: white;
                }
                
                @media (max-width: 991px) {
                    .invite-participants-modal .ant-modal {
                        width: 95% !important;
                        max-width: 95% !important;
                        margin: 10px auto !important;
                    }
                    
                    .invite-modal-header {
                        padding: 16px 20px 12px 20px !important;
                    }
                    
                    .invite-modal-header h4.ant-typography {
                        font-size: 18px !important;
                    }
                    
                    .invite-modal-header span {
                        font-size: 12px !important;
                    }
                    
                    .invite-modal-content {
                        padding: 20px 16px !important;
                    }
                    
                    .form-section .ant-card-body {
                        padding: 16px !important;
                    }
                    
                    .form-section h5.ant-typography {
                        font-size: 16px !important;
                    }
                    
                    .ant-row {
                        margin-left: 0 !important;
                        margin-right: 0 !important;
                    }
                    
                    .ant-col {
                        padding-left: 0 !important;
                        padding-right: 0 !important;
                    }
                }
                
                @media (max-width: 767px) {
                    .invite-participants-modal .ant-modal {
                        width: 98% !important;
                        max-width: 98% !important;
                    }
                    
                    .invite-modal-header {
                        padding: 12px 16px 10px 16px !important;
                    }
                    
                    .invite-modal-header h4.ant-typography {
                        font-size: 16px !important;
                    }
                    
                    .invite-modal-content {
                        padding: 16px 12px !important;
                    }
                    
                    .form-section .ant-card-body {
                        padding: 12px !important;
                    }
                    
                    .form-section h5.ant-typography {
                        font-size: 14px !important;
                    }
                    
                    .ant-form-item-label > label {
                        font-size: 13px !important;
                    }
                    
                    .ant-input-affix-wrapper,
                    .ant-input {
                        font-size: 14px !important;
                    }
                    
                    .ant-btn {
                        width: 100% !important;
                        min-width: auto !important;
                    }
                    
                    .ant-list-item {
                        flex-direction: column !important;
                        align-items: flex-start !important;
                    }
                    
                    .ant-list-item-action {
                        margin-left: 0 !important;
                        margin-top: 8px !important;
                        width: 100% !important;
                    }
                    
                    .ant-list-item-action li {
                        width: 100% !important;
                    }
                    
                    .ant-list-item-action .ant-btn {
                        width: 100% !important;
                    }
                }
                
                @media (max-width: 480px) {
                    .invite-modal-header h4.ant-typography {
                        font-size: 14px !important;
                    }
                    
                    .invite-modal-header span {
                        font-size: 11px !important;
                    }
                    
                    .form-section h5.ant-typography {
                        font-size: 13px !important;
                    }
                    
                    .ant-form-item-label > label {
                        font-size: 12px !important;
                    }
                }
            `}</style>
        </Modal>
    );
};

export default InviteParticipants; 